import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTrash, faSpinner, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site } from '@/types';

const SitooIntegration: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    product_create: false,
    update_stock: false,
    warehouse_id: '',
    product_create_latest_poll_time: '',
    update_stock_latest_poll_time: '',
  });

  const [alerts, setAlerts] = useState({
    saveSuccess: false,
    deleteSuccess: false,
    error: false,
  });

  const queryClient = useQueryClient();

  // Fetch sites
  const { data: sites = [] } = useQuery('sites', apiService.getSites);
  const siteId = sites.length > 0 ? sites[0].id : null;

  // Fetch Sitoo integration settings
  const { data: settings, isLoading } = useQuery(
    ['sitooIntegrationSettings', siteId],
    () => apiService.getSitooIntegrationSettings(siteId!),
    {
      enabled: !!siteId,
    }
  );

  // Handle form data population when settings change
  useEffect(() => {
    if (siteId && settings !== undefined) {
      // Only set form data if we have actual settings (not empty/null)
      if (settings && settings.id !== undefined && settings.id !== null) {
        setFormData({
          site_id: settings.site_id?.toString() || '',
          product_create: settings.product_create || false,
          update_stock: settings.update_stock || false,
          warehouse_id: settings.warehouse_id || '',
          product_create_latest_poll_time: settings.product_create_latest_poll_time || '',
          update_stock_latest_poll_time: settings.update_stock_latest_poll_time || '',
        });
      } else {
        // No existing data, set empty form for POST request
        setFormData({
          site_id: siteId.toString(),
          product_create: false,
          update_stock: false,
          warehouse_id: '',
          product_create_latest_poll_time: '',
          update_stock_latest_poll_time: '',
        });
      }
    }
  }, [settings, siteId]);

  // Save mutation
  const saveMutation = useMutation(
    (data: any) => {
      if (settings && settings.id !== undefined && settings.id !== null) {
        return apiService.updateSitooIntegrationSettings(siteId!, data);
      } else {
        return apiService.createSitooIntegrationSettings(siteId!, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sitooIntegrationSettings', siteId]);
        showAlert('saveSuccess');
      },
      onError: (error) => {
        console.error('Save failed:', error);
        showAlert('error');
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteSitooIntegrationSettings(siteId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sitooIntegrationSettings', siteId]);
        setFormData({
          site_id: '',
          product_create: false,
          update_stock: false,
          warehouse_id: '',
          product_create_latest_poll_time: '',
          update_stock_latest_poll_time: '',
        });
        showAlert('deleteSuccess');
      },
      onError: (error) => {
        console.error('Delete failed:', error);
        showAlert('error');
      },
    }
  );

  const showAlert = (type: 'saveSuccess' | 'deleteSuccess' | 'error') => {
    setAlerts(prev => ({
      ...prev,
      [type]: true,
    }));

    setTimeout(() => {
      setAlerts(prev => ({
        ...prev,
        [type]: false,
      }));
    }, 3000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      site_id: parseInt(formData.site_id),
    };

    saveMutation.mutate(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Sitoo Integration Settings</h1>
      </div>

      {/* Alerts */}
      {alerts.saveSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Sitoo Integration Settings saved</span>
        </div>
      )}

      {alerts.deleteSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Sitoo Integration Settings deleted</span>
        </div>
      )}

      {alerts.error && (
        <div className="alert alert-danger flex items-center space-x-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4" />
          <span>Something went wrong</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-row">
            <div className="form-column">
              <div>
                <label className="form-label form-label-required">Site:</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="">Select Site</option>
                  {sites.map((site: Site) => (
                    <option key={site.id} value={site.id}>
                      {site.url}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Warehouse ID:</label>
                <input
                  type="text"
                  value={formData.warehouse_id}
                  onChange={(e) => handleInputChange('warehouse_id', e.target.value)}
                  className="form-input"
                  placeholder="Enter warehouse ID"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="product_create"
                    checked={formData.product_create}
                    onChange={(e) => handleInputChange('product_create', e.target.checked)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="product_create" className="ml-2 text-sm font-medium text-gray-700">
                    Create Products Woo→Sitoo
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="update_stock"
                    checked={formData.update_stock}
                    onChange={(e) => handleInputChange('update_stock', e.target.checked)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="update_stock" className="ml-2 text-sm font-medium text-gray-700">
                    Update Products Sitoo→Woo
                  </label>
                </div>
              </div>
            </div>

            <div className="form-column">
              <div>
                <label className="form-label">Latest time Create Products Sitoo→Woo:</label>
                <input
                  type="datetime-local"
                  value={formatDateTime(formData.product_create_latest_poll_time)}
                  onChange={(e) => {
                    const isoString = e.target.value ? new Date(e.target.value).toISOString() : '';
                    handleInputChange('product_create_latest_poll_time', isoString);
                  }}
                  className="form-input"
                  placeholder="Select date and time"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Format: YYYY-MM-DD HH:mm:ss
                </p>
              </div>

              <div>
                <label className="form-label">Latest time Update Stock Sitoo→Woo:</label>
                <input
                  type="datetime-local"
                  value={formatDateTime(formData.update_stock_latest_poll_time)}
                  onChange={(e) => {
                    const isoString = e.target.value ? new Date(e.target.value).toISOString() : '';
                    handleInputChange('update_stock_latest_poll_time', isoString);
                  }}
                  className="form-input"
                  placeholder="Select date and time"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Format: YYYY-MM-DD HH:mm:ss
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 space-x-3">
            {settings && settings.id !== undefined && settings.id !== null && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            )}

            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SitooIntegration;
